import type { FC } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import styles from "../TaigaPage.module.scss"

type SetDueDateModalProps = {
	onClose: () => void
	onSubmit: (daysFromToday: number) => void
}

// ラジオで選べる日数 (0=今日, 1=明日, ... 10=10日後)。一番多い「1」をデフォルトにする。
const RADIO_DAYS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const
const DEFAULT_DAYS = 1
const MAX_DAYS = 365

const formatDayLabel = (days: number) => {
	if (days === 0) return "今日"
	if (days === 1) return "明日"
	return `${days}日後`
}

// selection が数値ならラジオ選択、"custom" なら数値入力 (11以上) を表す。
type Selection = number | "custom"

const SetDueDateModal: FC<SetDueDateModalProps> = ({ onClose, onSubmit }) => {
	const [selection, setSelection] = useState<Selection>(DEFAULT_DAYS)
	const [customValue, setCustomValue] = useState("11")
	const customInputRef = useRef<HTMLInputElement>(null)

	// 現在の選択値から確定する日数を計算する。無効なら null。
	const resolveDays = useCallback((): number | null => {
		if (selection !== "custom") return selection
		const parsed = Number.parseInt(customValue, 10)
		if (Number.isNaN(parsed) || parsed < 0 || parsed > MAX_DAYS) return null
		return parsed
	}, [selection, customValue])

	const handleSubmit = useCallback(() => {
		const days = resolveDays()
		if (days === null) return
		onSubmit(days)
	}, [resolveDays, onSubmit])

	// 「6以上」を選んだら数値入力に focus を移す。
	useEffect(() => {
		if (selection === "custom") {
			customInputRef.current?.focus()
			customInputRef.current?.select()
		}
	}, [selection])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault()
				onClose()
				return
			}
			// エンターで現在の選択を確定する (キーボードだけで完結させるため)。
			if (e.key === "Enter") {
				e.preventDefault()
				handleSubmit()
				return
			}
			// 数値入力に focus している間は数字キーをそのまま入力させる。
			const target = e.target
			const isNumberInput =
				target instanceof HTMLInputElement && target.type === "number"
			if (!isNumberInput && /^[0-9]$/.test(e.key)) {
				e.preventDefault()
				setSelection(Number.parseInt(e.key, 10))
			}
		},
		[onClose, handleSubmit]
	)

	const canSubmit = resolveDays() !== null

	return (
		<div
			className={styles.createMentionOverlay}
			onClick={onClose}
			onKeyDown={handleKeyDown}
		>
			<div
				className={styles.createMentionModal}
				onClick={(e) => e.stopPropagation()}
			>
				<h3 className={styles.createMentionTitle}>期限を設定</h3>

				<div className={styles.dueDateRadioGroup}>
					{RADIO_DAYS.map((days) => (
						<label key={days} className={styles.dueDateRadioOption}>
							<input
								type="radio"
								name="due-date-days"
								checked={selection === days}
								onChange={() => setSelection(days)}
								// biome-ignore lint/a11y/noAutofocus: デフォルト選択に focus してエンター即確定できるようにする
								autoFocus={days === DEFAULT_DAYS}
							/>
							<span className={styles.dueDateRadioNum}>{days}</span>
							<span className={styles.dueDateRadioSub}>
								{formatDayLabel(days)}
							</span>
						</label>
					))}
				</div>

				<div className={styles.dueDateCustomRow}>
					<label className={styles.dueDateRadioOption}>
						<input
							type="radio"
							name="due-date-days"
							checked={selection === "custom"}
							onChange={() => setSelection("custom")}
						/>
						<span className={styles.dueDateRadioNum}>11+</span>
						<span className={styles.dueDateRadioSub}>日後を指定</span>
					</label>
					<input
						ref={customInputRef}
						type="number"
						className={styles.createMentionPriorityManualInput}
						value={customValue}
						min={0}
						max={MAX_DAYS}
						onChange={(e) => setCustomValue(e.target.value)}
						onFocus={() => setSelection("custom")}
					/>
				</div>

				<div className={styles.createMentionActions}>
					<span className={styles.createMentionHint}>
						エンターで確定 / Esc でキャンセル
					</span>
					<button
						type="button"
						className={styles.createMentionCancelBtn}
						onClick={onClose}
					>
						キャンセル
					</button>
					<button
						type="button"
						className={styles.createMentionSubmitBtn}
						onClick={handleSubmit}
						disabled={!canSubmit}
					>
						設定
					</button>
				</div>
			</div>
		</div>
	)
}

export default SetDueDateModal
